import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  leadRulesApi,
  SCORING_FIELDS,
  SCORING_OPERATORS,
  type EscalationPolicy,
  type EscalationPolicyBody,
  type OwnershipAction,
  type OwnershipRule,
  type OwnershipRuleBody,
  type ScoringCondition,
  type ScoringRule,
  type ScoringRuleBody,
} from '../api/leadRules'
import { leadsApi, type LeadSourceOption } from '../api/leads'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  Page,
  PageHeader,
  Tabs,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  useSnackbar,
  useConfirm,
  FormSection,
  FormGrid,
} from '../components/ui'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'

/**
 * F-080 (TASK-0334): regole dei lead, solo Admin (la scrittura sul server è PolicyNames.Admin).
 * Tre schede: Punteggio (REQ-087-001), Proprietà sul cliente riacquisito (REQ-107-001), Escalation per
 * inattività (REQ-074-001). Nessuna regola è seminata: gli esempi della specifica compaiono solo come
 * suggerimento e non sono mai attivi finché l'Admin non li crea.
 * Dichiarato: le regole valgono per i lead creati o modificati dal CRM; non per importazioni CSV, acquisizione via
 * API e operazioni di massa. Nessuna riassegnazione automatica (coda comune).
 */
type Tab = 'scoring' | 'ownership' | 'escalation'

const TABS: { key: Tab; label: string }[] = [
  { key: 'scoring', label: 'Punteggio' },
  { key: 'ownership', label: 'Proprietà' },
  { key: 'escalation', label: 'Escalation' },
]

const ACTION_LABELS: Record<OwnershipAction, string> = {
  MantieniProprietario: 'Mantieni il proprietario',
  AssegnaNuovo: 'Assegna con instradamento',
  TeamLeader: 'Al responsabile del proprietario',
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function Modal({ title, supportingText, onClose, onSave, saving, error, children }: {
  title: string
  supportingText?: React.ReactNode
  onClose: () => void
  onSave: () => void
  saving: boolean
  error: string
  children: React.ReactNode
}) {
  return (
    <Dialog
      as="form"
      onSubmit={(e) => { e.preventDefault(); onSave() }}
      open
      onClose={onClose}
      title={title}
      supportingText={supportingText}
      actions={
        <>
          <Button variant="text" onClick={onClose}>Annulla</Button>
          <Button type="submit" variant="text" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </>
      }
    >
      <>
        {error && <Callout tone="error">{error}</Callout>}
        {children}
      </>
    </Dialog>
  )
}

function ActiveBadge({ active }: { active: boolean }) {
  return active
    ? <StatusPill tone="success">Attiva</StatusPill>
    : <StatusPill tone="neutral">Inattiva</StatusPill>
}

interface RowClone {
  label: string
  url: string
  name: string
  onCloned: (id: number) => void | Promise<void>
}

function RowActions({ onEdit, onDelete, clone }: { onEdit: () => void; onDelete: () => void; clone?: RowClone }) {
  return (
    <div className="d-flex gap-1 justify-content-end">
      {clone && (
        <CloneConfigurationButton
          entityLabel={clone.label}
          url={clone.url}
          defaultName={clone.name}
          note="La copia nasce disattivata: va attivata a mano dopo averla controllata."
          onCloned={clone.onCloned}
        />
      )}
      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={onEdit} />
      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={onDelete} />
    </div>
  )
}

function useLoader<T>(fetcher: () => Promise<T[]>) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await fetcher())
    } catch (e) {
      setError(errorText(e, 'Errore durante il caricamento'))
    } finally {
      setLoading(false)
    }
  }, [fetcher])
  useEffect(() => { void load() }, [load])
  return { items, loading, error, load }
}

// ------------------------------------------------------------------ Punteggio

const EMPTY_CONDITION: ScoringCondition = { field: 'lead_type', operator: 'eq', value: '' }

function parseConditions(raw: string): ScoringCondition[] {
  try {
    const parsed = JSON.parse(raw) as ScoringCondition[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function describeCondition(c: ScoringCondition): string {
  const field = SCORING_FIELDS.find(f => f.value === c.field)?.label.split(' (')[0] ?? c.field
  const op = SCORING_OPERATORS.find(o => o.value === c.operator)?.label ?? c.operator
  return `${field} ${op} "${c.value}"`
}

interface ScoringForm {
  name: string
  points: number
  sortOrder: number
  isActive: boolean
  conditions: ScoringCondition[]
}

const EMPTY_SCORING: ScoringForm = { name: '', points: 10, sortOrder: 0, isActive: true, conditions: [] }

function ScoringTab() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const { items, loading, error, load } = useLoader<ScoringRule>(leadRulesApi.scoring.list)
  const [form, setForm] = useState<ScoringForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [notice, setNotice] = useState('')
  const openAfterClone = useOpenAfterClone(items, r => openEdit(r))

  function openEdit(rule: ScoringRule) {
    setForm({
      name: rule.name, points: rule.points, sortOrder: rule.sortOrder, isActive: rule.isActive,
      conditions: parseConditions(rule.conditionsJson),
    })
    setEditId(rule.id)
    setSaveError('')
  }

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    if (form.points < -100 || form.points > 100) { setSaveError('I punti devono essere fra -100 e 100'); return }
    if (form.conditions.length > 5) { setSaveError('Al massimo 5 condizioni per regola'); return }
    const body: ScoringRuleBody = {
      name: form.name.trim(),
      conditionsJson: JSON.stringify(form.conditions),
      points: form.points,
      isActive: form.isActive,
      sortOrder: form.sortOrder,
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await leadRulesApi.scoring.update(editId, body)
      else await leadRulesApi.scoring.create(body)
      setForm(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(rule: ScoringRule) {
    if (!(await askConfirm({ title: 'Elimina regola', message: `Eliminare la regola "${rule.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await leadRulesApi.scoring.remove(rule.id)
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, "Errore durante l'eliminazione"), tone: 'error' })
    }
  }

  async function recalculate() {
    if (!(await askConfirm({ title: 'Ricalcola punteggi', message: 'Ricalcolare il punteggio di tutti i lead con le regole attive adesso?', confirmLabel: 'Ricalcola' }))) return
    try {
      const res = await leadRulesApi.scoring.recalculate()
      setNotice(`Ricalcolo avviato in background (job #${res.jobId}).`)
    } catch (e) {
      setNotice(errorText(e, 'Impossibile avviare il ricalcolo'))
    }
  }

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="text-muted md-typescale-body-small">
          Il punteggio è la somma dei punti delle regole attive che corrispondono al lead, limitata a 0-100, con la
          spiegazione. Senza regole attive i lead non hanno punteggio. Suggerimento dalla specifica: «+20 se è
          un'azienda», «−5 se manca l'email».
        </div>
        <div className="d-flex gap-2">
          <Button variant="outlined" size="sm" onClick={() => void recalculate()}>Ricalcola i lead</Button>
          <Button variant="tonal" size="sm" onClick={() => { setForm(EMPTY_SCORING); setEditId(null); setSaveError('') }}>
            Nuova regola
          </Button>
        </div>
      </div>
      {notice && (
        <Callout tone="info">
          {notice} <Link to="/jobs">Apri i job</Link>
        </Callout>
      )}
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState icon="tune" title="Nessuna regola di punteggio" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Condizioni</th><th>Punti</th>
                <th>Ordine</th><th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id}>
                  <td className="md-typescale-emphasized-body-medium">{r.name}</td>
                  <td className="md-typescale-body-small">
                    {parseConditions(r.conditionsJson).length === 0
                      ? <span className="text-muted">Sempre (nessuna condizione)</span>
                      : parseConditions(r.conditionsJson).map((c, i) => <div key={i}>{describeCondition(c)}</div>)}
                  </td>
                  <td className={`md-num ${r.points >= 0 ? 'text-success' : 'text-danger'}`}>{r.points > 0 ? '+' : ''}{r.points}</td>
                  <td className="text-muted md-num">{r.sortOrder}</td>
                  <td><ActiveBadge active={r.isActive} /></td>
                  <td className="text-end"><RowActions onEdit={() => openEdit(r)} onDelete={() => void remove(r)} clone={{ label: 'regola di punteggio', url: `/api/lead-rules/scoring/${r.id}/clone`, name: r.name, onCloned: async id => { await load(); openAfterClone(id) } }} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {form && (
        <Modal title={editId !== null ? 'Modifica regola di punteggio' : 'Nuova regola di punteggio'}
          onClose={() => { setForm(null); setEditId(null) }} onSave={() => void save()} saving={saving} error={saveError}>
          <FormSection title="Regola">
            <TextField className="md-span-full" label="Nome *" supportingText="Es. Azienda" value={form.name}
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <TextField label="Punti" type="number" min={-100} max={100} value={form.points}
              onChange={e => setForm(f => f && ({ ...f, points: parseInt(e.target.value, 10) || 0 }))} />
            <TextField label="Ordine" type="number" value={form.sortOrder}
              onChange={e => setForm(f => f && ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))} />
          </FormSection>
          <FormSection title="Condizioni (tutte devono valere, al massimo 5)" columns={1}>
            {form.conditions.length === 0 && <div className="text-muted md-typescale-body-small">Nessuna condizione: la regola vale per ogni lead.</div>}
            {form.conditions.map((c, idx) => (
              <FormGrid key={idx}>
                <Select label="Campo" labelHidden value={c.field}
                  onChange={e => setForm(f => f && ({ ...f, conditions: f.conditions.map((x, i) => i === idx ? { ...x, field: e.target.value } : x) }))}>
                  {SCORING_FIELDS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                <Select label="Operatore" labelHidden value={c.operator}
                  onChange={e => setForm(f => f && ({ ...f, conditions: f.conditions.map((x, i) => i === idx ? { ...x, operator: e.target.value } : x) }))}>
                  {SCORING_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                <div className="md-span-full d-flex gap-2 align-items-start">
                  <TextField className="flex-grow-1" label="Valore" labelHidden placeholder="Valore" value={c.value}
                    onChange={e => setForm(f => f && ({ ...f, conditions: f.conditions.map((x, i) => i === idx ? { ...x, value: e.target.value } : x) }))} />
                  <IconButton icon="close" size="sm" aria-label="Rimuovi condizione"
                    onClick={() => setForm(f => f && ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }))} />
                </div>
              </FormGrid>
            ))}
            <div>
              <Button variant="outlined" size="sm" disabled={form.conditions.length >= 5}
                onClick={() => setForm(f => f && ({ ...f, conditions: [...f.conditions, { ...EMPTY_CONDITION }] }))}>
                Aggiungi condizione
              </Button>
            </div>
            <div className="md-typescale-body-small text-muted">
              Dipendenti e SIM non esistono sul lead e non sono disponibili. Un attributo mancante vale come testo vuoto.
            </div>
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox id="sr-active" label="Attiva" checked={form.isActive}
              onChange={e => setForm(f => f && ({ ...f, isActive: e.target.checked }))} />
          </FormSection>
        </Modal>
      )}
      <RuleLifecyclePanel family="lead-scoring" onChanged={() => void load()} />
    </>
  )
}

// ------------------------------------------------------------------- Proprietà

interface OwnershipForm {
  name: string
  priority: number
  interestSector: string
  source: string
  minDays: number | ''
  maxDays: number | ''
  action: OwnershipAction
  isActive: boolean
}

const EMPTY_OWNERSHIP: OwnershipForm = {
  name: '', priority: 0, interestSector: '', source: '', minDays: '', maxDays: '', action: 'AssegnaNuovo', isActive: true,
}

function OwnershipTab({ sources }: { sources: LeadSourceOption[] }) {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const { items, loading, error, load } = useLoader<OwnershipRule>(leadRulesApi.ownership.list)
  const [form, setForm] = useState<OwnershipForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const openAfterClone = useOpenAfterClone(items, r => openEdit(r))

  function openEdit(r: OwnershipRule) {
    setForm({
      name: r.name, priority: r.priority, interestSector: r.interestSector ?? '', source: r.source ?? '',
      minDays: r.minDaysSinceOwnerActivity ?? '', maxDays: r.maxDaysSinceOwnerActivity ?? '',
      action: r.action, isActive: r.isActive,
    })
    setEditId(r.id)
    setSaveError('')
  }

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    if (form.minDays !== '' && form.maxDays !== '' && form.minDays > form.maxDays) {
      setSaveError('I giorni minimi non possono superare i massimi'); return
    }
    const body: OwnershipRuleBody = {
      name: form.name.trim(),
      action: form.action,
      priority: form.priority,
      interestSector: form.interestSector.trim() || null,
      source: form.source || null,
      minDaysSinceOwnerActivity: form.minDays === '' ? null : form.minDays,
      maxDaysSinceOwnerActivity: form.maxDays === '' ? null : form.maxDays,
      isActive: form.isActive,
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await leadRulesApi.ownership.update(editId, body)
      else await leadRulesApi.ownership.create(body)
      setForm(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(r: OwnershipRule) {
    if (!(await askConfirm({ title: 'Elimina regola', message: `Eliminare la regola "${r.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await leadRulesApi.ownership.remove(r.id)
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, "Errore durante l'eliminazione"), tone: 'error' })
    }
  }

  const sourceLabel = (code?: string | null) => (code ? sources.find(s => s.code === code)?.label ?? code : 'Qualsiasi')

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="text-muted md-typescale-body-small">
          Si applicano quando si crea un lead senza agente per un cliente che ha già un proprietario. Vince la regola con
          priorità più alta che corrisponde; se nessuna corrisponde il lead resta al proprietario. Senza regole attive
          nulla cambia. Suggerimento dalla specifica: «oltre 90 giorni dall'ultima attività → assegna con instradamento».
        </div>
        <Button variant="tonal" size="sm" onClick={() => { setForm(EMPTY_OWNERSHIP); setEditId(null); setSaveError('') }}>
          Nuova regola
        </Button>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState icon="tune" title="Nessuna regola di proprietà" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Priorità</th><th>Settore</th><th>Fonte</th>
                <th>Giorni dall'ultima attività</th><th>Azione</th><th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id}>
                  <td className="md-typescale-emphasized-body-medium">{r.name}</td>
                  <td className="text-muted md-num">{r.priority}</td>
                  <td>{r.interestSector ?? 'Qualsiasi'}</td>
                  <td>{sourceLabel(r.source)}</td>
                  <td>
                    {r.minDaysSinceOwnerActivity == null && r.maxDaysSinceOwnerActivity == null
                      ? 'Qualsiasi'
                      : `${r.minDaysSinceOwnerActivity ?? 0} - ${r.maxDaysSinceOwnerActivity ?? '∞'}`}
                  </td>
                  <td>{ACTION_LABELS[r.action] ?? r.action}</td>
                  <td><ActiveBadge active={r.isActive} /></td>
                  <td className="text-end"><RowActions onEdit={() => openEdit(r)} onDelete={() => void remove(r)} clone={{ label: 'regola di proprietà', url: `/api/lead-rules/ownership/${r.id}/clone`, name: r.name, onCloned: async id => { await load(); openAfterClone(id) } }} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {form && (
        <Modal title={editId !== null ? 'Modifica regola di proprietà' : 'Nuova regola di proprietà'}
          onClose={() => { setForm(null); setEditId(null) }} onSave={() => void save()} saving={saving} error={saveError}>
          <FormSection title="Regola">
            <TextField className="md-span-full" label="Nome *" supportingText="Es. Cliente fermo da oltre 90 giorni" value={form.name}
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <TextField className="md-span-full" label="Priorità" type="number" supportingText="Più alta vince." value={form.priority}
              onChange={e => setForm(f => f && ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))} />
          </FormSection>
          <FormSection title="Quando si applica"
            description="L'attività del proprietario è l'ultima creazione o modifica di una pratica del cliente. Senza pratiche i limiti sui giorni non corrispondono mai.">
            <TextField label="Settore di interesse" supportingText="Vuoto: qualsiasi" value={form.interestSector}
              onChange={e => setForm(f => f && ({ ...f, interestSector: e.target.value }))} />
            <Select label="Fonte" value={form.source} onChange={e => setForm(f => f && ({ ...f, source: e.target.value }))}>
              <option value="">Qualsiasi</option>
              {sources.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </Select>
            <TextField label="Giorni dall'ultima attività del proprietario: da (minimo)" type="number" min={0}
              supportingText="Vuoto: nessun minimo" value={form.minDays}
              onChange={e => setForm(f => f && ({ ...f, minDays: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))} />
            <TextField label="fino a (massimo)" type="number" min={0}
              supportingText="Vuoto: nessun massimo" value={form.maxDays}
              onChange={e => setForm(f => f && ({ ...f, maxDays: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))} />
          </FormSection>
          <FormSection title="Azione" columns={1}>
            <Select label="Azione" value={form.action}
              onChange={e => setForm(f => f && ({ ...f, action: e.target.value as OwnershipAction }))}>
              {(Object.keys(ACTION_LABELS) as OwnershipAction[]).map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
            </Select>
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox id="or-active" label="Attiva" checked={form.isActive}
              onChange={e => setForm(f => f && ({ ...f, isActive: e.target.checked }))} />
          </FormSection>
        </Modal>
      )}
    </>
  )
}

// ------------------------------------------------------------------ Escalation

interface EscalationForm {
  name: string
  source: string
  campaignId: number | ''
  agentMinutes: number | ''
  leaderMinutes: number | ''
  isActive: boolean
}

// 15/15 è l'esempio della specifica: solo un suggerimento da applicare con un clic, mai un valore salvato da solo.
const EMPTY_ESCALATION: EscalationForm = {
  name: '', source: '', campaignId: '', agentMinutes: '', leaderMinutes: '', isActive: true,
}

function EscalationTab({ sources }: { sources: LeadSourceOption[] }) {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const { items, loading, error, load } = useLoader<EscalationPolicy>(leadRulesApi.escalation.list)
  const [form, setForm] = useState<EscalationForm | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const openAfterClone = useOpenAfterClone(items, p => openEdit(p))

  function openEdit(p: EscalationPolicy) {
    setForm({
      name: p.name, source: p.source ?? '', campaignId: p.campaignId ?? '',
      agentMinutes: p.notifyAgentAfterMinutes, leaderMinutes: p.notifyTeamLeaderAfterMinutes, isActive: p.isActive,
    })
    setEditId(p.id)
    setSaveError('')
  }

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setSaveError('Il nome è obbligatorio'); return }
    if (form.agentMinutes === '' || form.agentMinutes < 1 || form.leaderMinutes === '' || form.leaderMinutes < 1) {
      setSaveError('Indica i minuti per entrambi gli avvisi (almeno 1)'); return
    }
    const body: EscalationPolicyBody = {
      name: form.name.trim(),
      notifyAgentAfterMinutes: form.agentMinutes,
      notifyTeamLeaderAfterMinutes: form.leaderMinutes,
      source: form.source || null,
      campaignId: form.campaignId === '' ? null : form.campaignId,
      isActive: form.isActive,
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) await leadRulesApi.escalation.update(editId, body)
      else await leadRulesApi.escalation.create(body)
      setForm(null)
      setEditId(null)
      void load()
    } catch (e) {
      setSaveError(errorText(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(p: EscalationPolicy) {
    if (!(await askConfirm({ title: 'Elimina politica', message: `Eliminare la politica "${p.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await leadRulesApi.escalation.remove(p.id)
      void load()
    } catch (e) {
      snackbar.show({ message: errorText(e, "Errore durante l'eliminazione"), tone: 'error' })
    }
  }

  const sourceLabel = (code?: string | null) => (code ? sources.find(s => s.code === code)?.label ?? code : 'Tutte')

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="text-muted md-typescale-body-small">
          Un lead nuovo o contattato, assegnato e senza attività (tentativo di chiamata o cambio di stato) dopo X minuti
          avvisa l'agente; dopo altri Y minuti avvisa il suo responsabile. Nessuna riassegnazione automatica. La verifica
          periodica è spenta finché non viene attivata sul server (<code>Leads:Escalation:Enabled</code>); senza
          politiche attive non fa nulla.
        </div>
        <Button variant="tonal" size="sm" onClick={() => { setForm(EMPTY_ESCALATION); setEditId(null); setSaveError('') }}>
          Nuova politica
        </Button>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined" padding="none">
        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState icon="tune" title="Nessuna politica di escalation" /> : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Fonte</th><th>Campagna</th><th>Avviso agente</th>
                <th>Avviso responsabile</th><th>Stato</th><th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td className="md-typescale-emphasized-body-medium">{p.name}</td>
                  <td>{sourceLabel(p.source)}</td>
                  <td>{p.campaignId ? `#${p.campaignId}` : 'Tutte'}</td>
                  <td>dopo {p.notifyAgentAfterMinutes} min</td>
                  <td>dopo altri {p.notifyTeamLeaderAfterMinutes} min (totale {p.notifyAgentAfterMinutes + p.notifyTeamLeaderAfterMinutes})</td>
                  <td><ActiveBadge active={p.isActive} /></td>
                  <td className="text-end"><RowActions onEdit={() => openEdit(p)} onDelete={() => void remove(p)} clone={{ label: 'politica di escalation', url: `/api/lead-rules/escalation/${p.id}/clone`, name: p.name, onCloned: async id => { await load(); openAfterClone(id) } }} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {form && (
        <Modal title={editId !== null ? 'Modifica politica di escalation' : 'Nuova politica di escalation'}
          onClose={() => { setForm(null); setEditId(null) }} onSave={() => void save()} saving={saving} error={saveError}>
          <FormSection title="Politica">
            <TextField className="md-span-full" label="Nome *" supportingText="Es. Lead web: 15 + 15 minuti" value={form.name}
              onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} />
            <Select label="Fonte" value={form.source} onChange={e => setForm(f => f && ({ ...f, source: e.target.value }))}>
              <option value="">Tutte</option>
              {sources.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </Select>
            <TextField label="Campagna (id)" type="number" min={1}
              supportingText="Vuoto: tutte. Con più politiche vale la più specifica (campagna, poi fonte)." value={form.campaignId}
              onChange={e => setForm(f => f && ({ ...f, campaignId: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '' }))} />
          </FormSection>
          <FormSection title="Tempi">
            <TextField label="Avvisa l'agente dopo (minuti) *" type="number" min={1} value={form.agentMinutes}
              onChange={e => setForm(f => f && ({ ...f, agentMinutes: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))} />
            <TextField label="Poi il responsabile dopo altri (minuti) *" type="number" min={1} value={form.leaderMinutes}
              onChange={e => setForm(f => f && ({ ...f, leaderMinutes: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))} />
            <div className="md-span-full">
              <Button variant="outlined" size="sm"
                onClick={() => setForm(f => f && ({ ...f, agentMinutes: 15, leaderMinutes: 15 }))}>
                Suggerimento della specifica: 15 + 15
              </Button>
            </div>
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox id="ep-active" label="Attiva" checked={form.isActive}
              onChange={e => setForm(f => f && ({ ...f, isActive: e.target.checked }))} />
          </FormSection>
        </Modal>
      )}
    </>
  )
}

// ------------------------------------------------------------------------ pagina

export function LeadRulesPage() {
  const [tab, setTab] = useState<Tab>('scoring')
  const [sources, setSources] = useState<LeadSourceOption[]>([])

  useEffect(() => {
    leadsApi.sources().then(setSources).catch(() => setSources([]))
  }, [])

  return (
    <Page>
      <PageHeader
        title="Regole dei lead"
        icon="rule_settings"
        tabs={
          <Tabs
            aria-label="Tipo di regola"
            items={TABS.map(t => ({ id: t.key, label: t.label }))}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />
      <Callout tone="info">
        Le regole valgono per i lead creati o modificati dal CRM. Non si applicano a importazioni CSV, acquisizione via
        API e operazioni di massa.
      </Callout>
      {tab === 'scoring' && <ScoringTab />}
      {tab === 'ownership' && <OwnershipTab sources={sources} />}
      {tab === 'escalation' && <EscalationTab sources={sources} />}
    </Page>
  )
}
